import {
  Brain,
  Check,
  ExternalLink,
  Key,
  Lock,
  TriangleAlert,
  Zap,
} from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";

import { PLAN_DISPLAY_LABEL } from "@domain/billing/plan-sync";
import {
  clampEffort,
  formatContextTokens,
  isModelLockedForPlan,
  METERS_AS_OF,
  MODEL_CATALOG,
  MODEL_COMPARE_URL,
  modelById,
  REASONING_EFFORT_META,
} from "@domain/model-catalog";
import { cn } from "@/lib/utils";

import type {
  CatalogModel,
  MeterScore,
  ReasoningEffort,
} from "@domain/model-catalog";
import type { Plan } from "@domain/claw-config";

/** Five-segment capability meter — deliberately coarse (curated take, not a
 *  fake benchmark decimal). Fills coral on the selected tile so the chosen
 *  brain's profile pops. */
function Meter({
  label,
  score,
  emphasized,
}: {
  label: string;
  score: MeterScore;
  emphasized: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40">
        {label}
      </span>
      <span className="flex gap-1" aria-label={`${label}: ${score} of 5`}>
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={cn(
              "h-1 w-3.5 rounded-full transition-colors",
              i <= score
                ? emphasized
                  ? "bg-brand-coral"
                  : "bg-black/50 dark:bg-white/50"
                : "bg-black/10 dark:bg-white/15",
            )}
          />
        ))}
      </span>
    </div>
  );
}

/** Portrait model tile — the brain's identity card: who it is, what it's for,
 *  its curated capability profile, and the two hard facts (context window,
 *  relative credit burn). */
function ModelTile({
  model,
  selected,
  locked,
  onSelect,
}: {
  model: CatalogModel;
  selected: boolean;
  locked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "relative flex h-full flex-col rounded-2xl border p-4 text-left transition-all",
        selected
          ? "border-brand-coral bg-brand-coral/[0.05] shadow-[0_12px_32px_-20px_rgba(0,0,0,0.4)]"
          : "border-black/10 hover:border-black/30 dark:border-white/10 dark:hover:border-white/30",
      )}
    >
      {selected && (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-brand-coral">
          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
        </span>
      )}
      {locked && !selected && (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-black/5 dark:bg-white/10">
          <Lock className="h-3 w-3 text-black/40 dark:text-white/40" strokeWidth={2.4} />
        </span>
      )}

      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-home-butter text-[12px] font-black text-home-ink">
        {model.provider.slice(0, 2).toUpperCase()}
      </span>
      <span className="mt-3 text-[15px] font-bold leading-tight text-black dark:text-white">
        {model.name}
      </span>
      <span className="mt-0.5 block truncate font-mono text-[10px] text-black/35 dark:text-white/35">
        {model.id}
      </span>
      <span className="mt-2 block text-[12px] leading-snug text-black/60 dark:text-white/60">
        {model.bestFor}
      </span>

      <span className="mt-4 block space-y-1.5">
        <Meter label="Reasoning" score={model.meters.reasoning} emphasized={selected} />
        <Meter label="Agentic" score={model.meters.agentic} emphasized={selected} />
        <Meter label="Speed" score={model.meters.speed} emphasized={selected} />
      </span>

      <span className="mt-auto block pt-4">
        <span className="flex flex-wrap items-center gap-1.5 border-t border-black/5 pt-3 dark:border-white/10">
          <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold text-black/55 dark:bg-white/10 dark:text-white/55">
            {formatContextTokens(model.contextTokens)} context
          </span>
          <span
            className={cn(
              "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold",
              model.creditMultiplier > 1
                ? "bg-home-butter text-home-ink"
                : "bg-black/5 text-black/55 dark:bg-white/10 dark:text-white/55",
            )}
          >
            <Zap className="h-2.5 w-2.5" strokeWidth={2.6} />
            {model.creditMultiplier}× credits
          </span>
        </span>
        {model.warning && (
          <span className="mt-2 flex items-start gap-1 text-[11px] font-semibold leading-snug text-amber-600 dark:text-amber-400">
            <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2.4} />
            {model.warning}
          </span>
        )}
      </span>
    </button>
  );
}

interface ModelSwitcherProps {
  /** The user's real billing plan — gates the "pro" tier (BYOK unlocks all).
   *  Display-side of the gate only; agentSettings.setReasoningConfig enforces
   *  the same catalog rule server-side. */
  plan: Plan;
  /** Current DRAFT selection — CONTROLLED. The dashboard wires this to the
   *  staged draft over the user's Convex record; dev previews wire local
   *  state. */
  modelId: string;
  effort: ReasoningEffort;
  onSelectModel: (modelId: string) => void;
  onSelectEffort: (effort: ReasoningEffort) => void;
  /** Transient status line for the re-braining toast ("restarting your
   *  agent", …). null/undefined hides the toast. The OWNER derives it from
   *  the real mutation outcome — this component never fakes progress. */
  switchingNote?: string | null;
  /** Invoked when the user confirms the upgrade path from a locked model's
   *  gate. Settings wires this to surface the Subscription plans; omit to make
   *  the gate purely informational. */
  onUpgrade?: () => void;
}

/**
 * The brain picker: portrait model tiles (identity + curated capability
 * meters + hard facts) with the thinking-depth control folded into the same
 * surface — one card configures the whole brain. Purely presentational +
 * controlled: selection staging, persistence, and the pod restart live with
 * the owner (AgentSection ↔ Convex). Catalog data (including the per-model
 * depth variants) comes from @domain/model-catalog — the same source the
 * server validates against. Meters are OUR curated take (stamped, coarse by
 * design); the LMArena link is the live-numbers escape hatch.
 */
export function ModelSwitcher({
  plan,
  modelId,
  effort,
  onSelectModel,
  onSelectEffort,
  switchingNote,
  onUpgrade,
}: ModelSwitcherProps) {
  const [gateModel, setGateModel] = useState<CatalogModel | null>(null);

  // The depth variants on offer are the SELECTED model's (they differ per
  // model — catalog authority). Display-clamp so a transient unsupported
  // pair (mid model-switch) never highlights a pill the model lacks.
  const currentModel = modelById(modelId);
  const effectiveEffort = clampEffort({ model: currentModel, effort });

  const pick = (m: CatalogModel) => {
    if (isModelLockedForPlan(m, plan)) {
      setGateModel(m);
      return;
    }
    if (m.id !== modelId) onSelectModel(m.id);
  };

  return (
    <div>
      {plan === "byok" && (
        <p className="mb-3 flex items-center gap-1.5 rounded-lg bg-accent-green/[0.08] px-3 py-2 text-[12px] font-medium text-black/60 dark:text-white/60">
          <Key className="h-3.5 w-3.5 shrink-0 text-accent-green" strokeWidth={2.2} />
          Your key, every model — inference bills to your provider.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {MODEL_CATALOG.map((m) => (
          <ModelTile
            key={m.id}
            model={m}
            selected={m.id === modelId}
            locked={isModelLockedForPlan(m, plan)}
            onSelect={() => pick(m)}
          />
        ))}
      </div>

      {/* Thinking depth — the second axis, scoped to the selected brain. */}
      <div className="mt-5 rounded-2xl bg-black/[0.03] p-4 dark:bg-white/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-black dark:text-white">
              Thinking depth
            </div>
            <div className="text-[11px] leading-snug text-black/45 dark:text-white/45">
              {REASONING_EFFORT_META[effectiveEffort].blurb}
            </div>
          </div>
          <div
            role="radiogroup"
            aria-label="Thinking depth"
            className="inline-flex shrink-0 rounded-full bg-black/5 p-0.5 dark:bg-white/10"
          >
            {currentModel.efforts.map((e) => (
              <button
                key={e.id}
                type="button"
                role="radio"
                aria-checked={effectiveEffort === e.id}
                onClick={() => {
                  if (e.id !== effectiveEffort) onSelectEffort(e.id);
                }}
                className={cn(
                  "rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors",
                  effectiveEffort === e.id
                    ? "bg-white text-black shadow-sm dark:bg-surface-dark-card dark:text-white"
                    : "text-black/55 hover:text-black/80 dark:text-white/55 dark:hover:text-white/80",
                )}
              >
                {REASONING_EFFORT_META[e.id].label}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-2 text-[10px] leading-snug text-black/35 dark:text-white/35">
          {currentModel.name} offers {currentModel.efforts.length} depth
          {currentModel.efforts.length > 1 ? "s" : ""} — deeper thinking uses
          more tokens per reply.
        </p>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1">
        <p className="text-[10px] leading-snug text-black/35 dark:text-white/35">
          Meters are our curated take ({METERS_AS_OF}) — coarse on purpose.
        </p>
        <a
          href={MODEL_COMPARE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-coral hover:underline"
        >
          Live rankings on the Arena leaderboard
          <ExternalLink className="h-3 w-3" strokeWidth={2.4} />
        </a>
      </div>

      <UpgradeGate
        model={gateModel}
        onUpgrade={onUpgrade}
        onClose={() => setGateModel(null)}
      />
      <SwitchingToast modelId={modelId} effort={effort} note={switchingNote} />
    </div>
  );
}

/** The plan gate shown when a user picks a locked frontier model — the ported
 *  Studio upgrade-dialog pattern. */
function UpgradeGate({
  model,
  onUpgrade,
  onClose,
}: {
  model: CatalogModel | null;
  onUpgrade?: () => void;
  onClose: () => void;
}) {
  if (!model) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-[0_32px_80px_-30px_rgba(0,0,0,0.5)] dark:bg-surface-dark-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Frontier models require an upgrade"
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-home-butter">
          <Lock className="h-5 w-5 text-home-ink" strokeWidth={2.2} />
        </span>
        <h2 className="mt-4 font-display text-[19px] font-semibold tracking-tight text-home-ink dark:text-white">
          {model.name} is a frontier model
        </h2>
        <p className="mt-2 font-body text-[14px] leading-relaxed text-home-ink/60 dark:text-white/60">
          Frontier brains burn {model.creditMultiplier}× credits, so they unlock
          on the {PLAN_DISPLAY_LABEL.byok} plan — bring your own key and every
          model is available, billed by your provider.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          {onUpgrade && (
            <button
              type="button"
              onClick={() => {
                onUpgrade();
                onClose();
              }}
              className="inline-flex min-h-[46px] items-center justify-center rounded-full bg-home-yellow font-body text-[14px] font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              See plans
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[42px] items-center justify-center rounded-full font-body text-[13px] font-semibold text-home-ink/60 transition-colors hover:text-home-ink dark:text-white/60 dark:hover:text-white"
          >
            Not now
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/** The re-braining toast — shown while `note` is set, reflecting the REAL
 *  outcome of the change (persisted + pod restarting / applies later /
 *  failed), never a synthetic timer-only state. */
function SwitchingToast({
  modelId,
  effort,
  note,
}: {
  modelId: string;
  effort: ReasoningEffort;
  note?: string | null;
}) {
  if (!note) return null;
  const current = modelById(modelId);
  const effortLabel =
    REASONING_EFFORT_META[clampEffort({ model: current, effort })].label;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2.5 rounded-full bg-home-ink px-4 py-2.5 text-[13px] font-semibold text-home-cream shadow-[0_16px_40px_-16px_rgba(0,0,0,0.6)]"
    >
      <Brain className="h-4 w-4 animate-pulse text-home-butter" strokeWidth={2.2} />
      {current.name} · {effortLabel} — {note}
    </motion.div>
  );
}
